import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, Plus, Copy, Check, Trash2, Loader2 } from 'lucide-react';
import { apiKeyApi, type NewApiKey } from '@/lib/api';
import { formatDate, copyToClipboard, cn } from '@/lib/utils';

export function ApiKeys() {
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState<NewApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyName, setKeyName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: apiKeyApi.list,
  });

  const createMutation = useMutation({
    mutationFn: (name?: string) => apiKeyApi.create(name),
    onSuccess: (data) => {
      setNewKey(data.apiKey);
      setShowCreateModal(false);
      setKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: apiKeyApi.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCopyKey = async () => {
    if (newKey) {
      await copyToClipboard(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const apiKeys = data?.apiKeys || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">API Keys</h1>
          <p className="text-slate-600">Manage your API keys for programmatic access</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          Create API Key
        </button>
      </div>

      {/* New Key Display */}
      {newKey && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-800">API Key Created</p>
              <p className="text-sm text-green-700 mb-3">
                Copy this key now. It will not be shown again!
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border rounded font-mono text-sm truncate">
                  {newKey.key}
                </code>
                <button
                  onClick={handleCopyKey}
                  className={cn(
                    'px-3 py-2 rounded font-medium text-sm flex items-center gap-1',
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-800 text-white hover:bg-slate-700'
                  )}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button
              onClick={() => setNewKey(null)}
              className="text-green-600 hover:text-green-800"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Your API Keys</h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="p-8 text-center">
            <Key className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No API keys</p>
            <p className="text-slate-500 text-sm">Create an API key to access the API programmatically</p>
          </div>
        ) : (
          <div className="divide-y">
            {apiKeys.map((key) => (
              <div key={key.id} className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{key.name}</p>
                  <p className="text-sm text-slate-500">
                    <code className="bg-slate-100 px-1 rounded">{key.prefix}...</code>
                    {' | '}
                    Created {formatDate(key.createdAt)}
                    {key.lastUsedAt && ` | Last used ${formatDate(key.lastUsedAt)}`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to revoke this API key?')) {
                      revokeMutation.mutate(key.id);
                    }
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Revoke key"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Documentation */}
      <div className="mt-8 bg-slate-50 rounded-xl border p-6">
        <h3 className="font-semibold mb-3">API Usage</h3>
        <p className="text-sm text-slate-600 mb-4">
          Use your API key to authenticate requests to the SitemapHost API.
        </p>
        <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-slate-100 overflow-x-auto">
          <pre>{`curl -X POST https://dash.sitemaphost.app/api/sitemap/generate \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "domain": "sitemap.yoursite.com",
    "urls": [
      {"loc": "https://yoursite.com/page-1"},
      {"loc": "https://yoursite.com/page-2"}
    ]
  }'`}</pre>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Create API Key</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Key Name (optional)
              </label>
              <input
                type="text"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., Production, CI/CD, Development"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 border rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(keyName || undefined)}
                disabled={createMutation.isPending}
                className="flex-1 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
